import { Controller } from "@hotwired/stimulus"
import { apiGet, formatMoney, t } from "../api_helper"
import { icon } from "../lib/icons"
import { isOfflineError, showOfflinePlaceholder } from "../lib/offline_ui"
import "../lib/paywall"

export default class extends Controller {
  static targets = [
    "chart", "legend", "total", "totalLabel", "chartCenter", "customRange", "startDate", "endDate",
    "tabBtn", "categoriesView", "comparisonView", "trendsView",
    "comparisonTotal", "comparisonCategories", "comparisonMonthPickers",
    "trendsChart", "trendsSummary", "trendsBreakdown", "trendsPremiumGate"
  ]

  connect() {
    this.currentPeriod = "month"
    this.currentTab = "categories"
    this.currentMonths = 6
    this.customStartDate = null
    this.customEndDate = null
    this.isPremium = null

    // Comparison month selection: default current vs previous
    const now = new Date()
    this.compCurrentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    this.compPreviousMonth = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`

    this.load()
  }

  // ── PWA colors ──

  get hintColor() { return "rgb(148, 163, 184)" }
  get buttonColor() { return "rgb(99, 102, 241)" }
  get expenseColor() { return "rgb(239, 68, 68)" }
  get incomeColor() { return "rgb(16, 185, 129)" }
  get secondaryBgColor() { return "rgb(248, 250, 252)" }
  get bgColor() { return "rgb(241, 245, 249)" }

  // ── Tab switching ──

  switchTab(event) {
    const tab = event.currentTarget.dataset.tab
    if (tab === this.currentTab) return

    this.currentTab = tab
    this.tabBtnTargets.forEach(btn => {
      const isActive = btn.dataset.tab === tab
      btn.style.backgroundColor = isActive ? "#ffffff" : "transparent"
      btn.style.color = isActive ? "#1e293b" : this.hintColor
      btn.style.boxShadow = isActive ? "0 1px 3px rgba(0,0,0,0.08)" : "none"
    })

    if (tab === "categories") {
      this.categoriesViewTarget.classList.remove("hidden")
      if (this.hasComparisonViewTarget) this.comparisonViewTarget.classList.add("hidden")
      this.trendsViewTarget.classList.add("hidden")
    } else if (tab === "comparison") {
      this.categoriesViewTarget.classList.add("hidden")
      if (this.hasComparisonViewTarget) this.comparisonViewTarget.classList.remove("hidden")
      this.trendsViewTarget.classList.add("hidden")
      this.loadComparison()
    } else {
      this.categoriesViewTarget.classList.add("hidden")
      if (this.hasComparisonViewTarget) this.comparisonViewTarget.classList.add("hidden")
      this.trendsViewTarget.classList.remove("hidden")
      this.loadTrends()
    }
  }

  // ── Categories view ──

  switchPeriod(event) {
    const btn = event.currentTarget
    this.currentPeriod = btn.dataset.period

    this.categoriesViewTarget.querySelectorAll("[data-period]").forEach(el => {
      el.style.backgroundColor = this.secondaryBgColor
      el.style.color = this.hintColor
    })
    btn.style.backgroundColor = this.buttonColor
    btn.style.color = "#ffffff"

    if (this.currentPeriod === "custom") {
      if (this.hasCustomRangeTarget) {
        this.customRangeTarget.classList.remove("hidden")
        const today = new Date().toISOString().slice(0, 10)
        const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10)
        if (this.hasStartDateTarget && !this.startDateTarget.value) this.startDateTarget.value = monthAgo
        if (this.hasEndDateTarget && !this.endDateTarget.value) this.endDateTarget.value = today
        this.customStartDate = this.startDateTarget.value
        this.customEndDate = this.endDateTarget.value
      }
    } else {
      if (this.hasCustomRangeTarget) this.customRangeTarget.classList.add("hidden")
    }

    this.load()
  }

  applyCustomRange() {
    if (this.hasStartDateTarget && this.hasEndDateTarget) {
      this.customStartDate = this.startDateTarget.value
      this.customEndDate = this.endDateTarget.value
      if (this.customStartDate && this.customEndDate) this.load()
    }
  }

  async load() {
    try {
      let url = `/api/stats/by_category?period=${this.currentPeriod}`
      if (this.currentPeriod === "custom" && this.customStartDate && this.customEndDate) {
        url += `&start_date=${this.customStartDate}&end_date=${this.customEndDate}`
      }
      const data = await apiGet(url)
      this.currency = data.currency || "RUB"
      this.renderChart(data)
      this.renderLegend(data)
      if (this.hasTotalTarget) {
        this.totalTarget.textContent = formatMoney(data.total, this.currency)
        this.totalTarget.classList.add("amount-animate")
      }
      if (this.hasTotalLabelTarget) {
        this.totalLabelTarget.textContent = this.currentPeriod === "month"
          ? t("stats.monthly_expenses")
          : t("stats.expenses_for_period")
      }
    } catch (e) {
      if (isOfflineError(e)) { showOfflinePlaceholder(this.hasLegendTarget ? this.legendTarget : null, () => this.load()); return }
      console.error("Stats load error:", e)
    }
  }

  getChart() {
    return window.Chart
  }

  async renderChart(data) {
    if (!this.hasChartTarget || !data.categories.length) {
      if (this.hasChartTarget) {
        this.chartTarget.parentElement.innerHTML = `
          <div class="empty-state" style="padding: 60px 20px;">
            <span class="empty-state-icon">${icon("chart-bar", "w-8 h-8 text-slate-300 mx-auto")}</span>
            <div class="empty-state-text">${t("dashboard.no_transactions")}</div>
          </div>`
      }
      return
    }

    const Chart = this.getChart()
    const ctx = this.chartTarget.getContext("2d")
    if (this.chartInstance) this.chartInstance.destroy()

    this.chartInstance = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: data.categories.map(c => `${c.icon} ${c.name}`),
        datasets: [{
          data: data.categories.map(c => c.total),
          backgroundColor: data.categories.map(c => c.color),
          borderWidth: 0,
          spacing: 3,
          borderRadius: 6,
          hoverOffset: 6
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        cutout: "68%",
        animation: { animateRotate: true, duration: 600, easing: "easeOutCubic" },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "rgba(0,0,0,0.8)",
            cornerRadius: 12,
            padding: 12,
            titleFont: { size: 13, weight: "600" },
            bodyFont: { size: 12 },
            callbacks: {
              label: (ctx) => ` ${formatMoney(ctx.parsed, this.currency)} (${data.categories[ctx.dataIndex].percentage}%)`
            }
          }
        }
      }
    })
  }

  renderLegend(data) {
    if (!this.hasLegendTarget) return

    if (!data.categories.length) {
      this.legendTarget.innerHTML = `
        <div class="empty-state" style="padding: 20px;">
          <div class="empty-state-text">${t("dashboard.no_transactions")}</div>
        </div>`
      return
    }

    this.legendTarget.innerHTML = data.categories.map((c, i) => `
      <div class="flex items-center justify-between py-2.5 ${i < data.categories.length - 1 ? 'border-b' : ''}"
           style="border-color: color-mix(in srgb, ${this.hintColor} 10%, transparent);">
        <div class="flex items-center gap-3">
          <div class="flex-shrink-0" style="width: 10px; height: 10px; border-radius: 50%; background-color: ${c.color};"></div>
          <span class="text-sm">${c.icon} ${c.name}</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-sm font-semibold">${formatMoney(c.total, this.currency)}</span>
          <span class="text-xs px-1.5 py-0.5 rounded-full" style="background-color: color-mix(in srgb, ${c.color} 10%, transparent); color: ${c.color}; font-weight: 600;">${c.percentage}%</span>
        </div>
      </div>
    `).join("")
  }

  // ── Comparison view ──

  renderMonthPickers() {
    if (!this.hasComparisonMonthPickersTarget) return

    const locale = window.APP_LOCALE === "ru" ? "ru-RU" : "en-US"
    const now = new Date()
    const months = []
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
      const label = d.toLocaleDateString(locale, { month: "short", year: "2-digit" })
      months.push({ key, label })
    }

    const renderPills = (selected, side) => months.map(m => {
      const isActive = m.key === selected
      const bg = isActive ? this.buttonColor : this.secondaryBgColor
      const color = isActive ? "#ffffff" : this.hintColor
      return `<button data-action="click->pwa-stats#selectCompMonth"
                      data-side="${side}" data-month="${m.key}"
                      class="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap active:scale-95"
                      style="background-color: ${bg}; color: ${color}; text-transform: capitalize;">
                ${m.label}
              </button>`
    }).join("")

    this.comparisonMonthPickersTarget.innerHTML = `
      <div class="mb-3">
        <div class="text-xs font-medium mb-1.5" style="color: ${this.hintColor};">${t("stats.period_a") || "Period A"}</div>
        <div class="flex gap-1.5 overflow-x-auto pb-1" style="scrollbar-width: none;">${renderPills(this.compPreviousMonth, "previous")}</div>
      </div>
      <div class="mb-3">
        <div class="text-xs font-medium mb-1.5" style="color: ${this.hintColor};">${t("stats.period_b") || "Period B"}</div>
        <div class="flex gap-1.5 overflow-x-auto pb-1" style="scrollbar-width: none;">${renderPills(this.compCurrentMonth, "current")}</div>
      </div>`
  }

  selectCompMonth(event) {
    const side = event.currentTarget.dataset.side
    const month = event.currentTarget.dataset.month
    if (side === "previous") {
      this.compPreviousMonth = month
    } else {
      this.compCurrentMonth = month
    }
    this.renderMonthPickers()
    this.loadComparison()
  }

  async loadComparison() {
    this.renderMonthPickers()
    try {
      const data = await apiGet(`/api/stats/comparison?previous_month=${this.compPreviousMonth}&current_month=${this.compCurrentMonth}`)
      this.comparisonCurrency = data.currency || "RUB"
      this.renderComparisonTotal(data)
      this.renderComparisonCategories(data)
    } catch (e) {
      if (isOfflineError(e)) { showOfflinePlaceholder(this.hasComparisonCategoriesTarget ? this.comparisonCategoriesTarget : null, () => this.loadComparison()); return }
      console.error("Comparison load error:", e)
    }
  }

  renderComparisonTotal(data) {
    if (!this.hasComparisonTotalTarget) return

    const change = data.total_change
    const changeColor = change === null ? this.hintColor : (change <= 0 ? this.incomeColor : this.expenseColor)
    const changeText = change === null ? "" : `${change > 0 ? "+" : ""}${change}%`
    const changeIcon = change === null ? "" : (change <= 0 ? "↓" : "↑")

    const locale = window.APP_LOCALE === "ru" ? "ru-RU" : "en-US"
    const prevDate = new Date(data.previous_period.start + "T00:00:00")
    const currDate = new Date(data.current_period.start + "T00:00:00")
    const prevMonth = prevDate.toLocaleDateString(locale, { month: "long" })
    const currMonth = currDate.toLocaleDateString(locale, { month: "long" })

    this.comparisonTotalTarget.innerHTML = `
      <div class="flex justify-around items-center mb-3">
        <div class="text-center">
          <div class="text-xs font-medium mb-1" style="color: ${this.hintColor}; text-transform: capitalize;">${prevMonth}</div>
          <div class="text-lg font-bold" style="color: ${this.expenseColor};">${formatMoney(data.previous_total, this.comparisonCurrency)}</div>
        </div>
        <div style="font-size: 20px; color: ${this.hintColor};">→</div>
        <div class="text-center">
          <div class="text-xs font-medium mb-1" style="color: ${this.hintColor}; text-transform: capitalize;">${currMonth}</div>
          <div class="text-lg font-bold" style="color: ${this.expenseColor};">${formatMoney(data.current_total, this.comparisonCurrency)}</div>
        </div>
      </div>
      ${change !== null ? `
        <div class="text-center">
          <span class="px-3 py-1 rounded-full text-sm font-bold" style="background-color: color-mix(in srgb, ${changeColor} 12%, transparent); color: ${changeColor};">
            ${changeIcon} ${changeText}
          </span>
        </div>` : ""}`
  }

  renderComparisonCategories(data) {
    if (!this.hasComparisonCategoriesTarget) return

    if (!data.categories.length) {
      this.comparisonCategoriesTarget.innerHTML = `
        <div class="empty-state" style="padding: 20px;">
          <div class="empty-state-text">${t("dashboard.no_transactions")}</div>
        </div>`
      return
    }

    const maxAmount = Math.max(...data.categories.map(c => Math.max(c.current, c.previous)))

    this.comparisonCategoriesTarget.innerHTML = data.categories.map((c, i) => {
      const currentPct = maxAmount > 0 ? (c.current / maxAmount * 100) : 0
      const previousPct = maxAmount > 0 ? (c.previous / maxAmount * 100) : 0
      const change = c.change
      const changeColor = change === null ? this.hintColor : (change <= 0 ? this.incomeColor : this.expenseColor)
      const changeText = change === null ? (t("stats.new_category") || "New") : `${change > 0 ? "+" : ""}${change}%`
      const border = i < data.categories.length - 1
        ? `border-bottom: 1px solid color-mix(in srgb, ${this.hintColor} 10%, transparent);`
        : ""

      return `
        <div style="padding: 12px 0; ${border}">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-2">
              <span style="font-size: 18px;">${c.icon}</span>
              <span class="text-sm font-medium">${c.name}</span>
            </div>
            <span class="text-xs font-bold px-2 py-0.5 rounded-full"
                  style="background-color: color-mix(in srgb, ${changeColor} 12%, transparent); color: ${changeColor};">
              ${changeText}
            </span>
          </div>
          <div class="flex flex-col gap-1.5">
            <div class="flex items-center gap-2">
              <div class="text-xs" style="color: ${this.hintColor}; width: 50px;">${t("stats.previous") || "Prev"}</div>
              <div class="flex-1" style="height: 8px; border-radius: 4px; background-color: ${this.secondaryBgColor}; overflow: hidden;">
                <div style="height: 100%; width: ${previousPct}%; border-radius: 4px; background-color: color-mix(in srgb, ${c.color} 40%, transparent); transition: width 0.5s;"></div>
              </div>
              <div class="text-xs font-medium" style="width: 75px; text-align: right;">${formatMoney(c.previous, this.comparisonCurrency)}</div>
            </div>
            <div class="flex items-center gap-2">
              <div class="text-xs" style="color: ${this.hintColor}; width: 50px;">${t("stats.current") || "Now"}</div>
              <div class="flex-1" style="height: 8px; border-radius: 4px; background-color: ${this.secondaryBgColor}; overflow: hidden;">
                <div style="height: 100%; width: ${currentPct}%; border-radius: 4px; background-color: ${c.color}; transition: width 0.5s;"></div>
              </div>
              <div class="text-xs font-semibold" style="width: 75px; text-align: right;">${formatMoney(c.current, this.comparisonCurrency)}</div>
            </div>
          </div>
        </div>`
    }).join("")
  }

  // ── Trends view ──

  switchMonths(event) {
    const btn = event.currentTarget
    this.currentMonths = parseInt(btn.dataset.months)

    this.trendsViewTarget.querySelectorAll("[data-months]").forEach(el => {
      el.style.backgroundColor = this.secondaryBgColor
      el.style.color = this.hintColor
    })
    btn.style.backgroundColor = this.buttonColor
    btn.style.color = "#ffffff"

    this.loadTrends()
  }

  async loadTrends() {
    // Check premium status once
    if (this.isPremium === null) {
      try {
        const sub = await apiGet("/api/subscription")
        this.isPremium = !!sub.premium
      } catch {
        this.isPremium = false
      }
    }

    if (!this.isPremium) {
      this.showPremiumGate()
      return
    }

    try {
      const data = await apiGet(`/api/stats/trends?months=${this.currentMonths}`)
      this.trendsCurrency = data.currency || "RUB"
      this.renderTrendsChart(data.trends)
      this.renderTrendsSummary(data.trends)
      this.renderTrendsBreakdown(data.trends)
    } catch (e) {
      if (isOfflineError(e)) { showOfflinePlaceholder(this.hasTrendsChartTarget ? this.trendsChartTarget.parentElement : null, () => this.loadTrends()); return }
      console.error("Trends load error:", e)
    }
  }

  showPremiumGate() {
    if (this.hasTrendsPremiumGateTarget) {
      this.trendsPremiumGateTarget.classList.remove("hidden")
    }
    // Hide chart and summary
    if (this.hasTrendsChartTarget) {
      const chartCard = this.trendsChartTarget.closest(".card") || this.trendsChartTarget.parentElement
      if (chartCard) chartCard.classList.add("hidden")
    }
    if (this.hasTrendsSummaryTarget) this.trendsSummaryTarget.classList.add("hidden")
    if (this.hasTrendsBreakdownTarget) this.trendsBreakdownTarget.classList.add("hidden")
    // Hide months switcher
    this.trendsViewTarget.querySelector(".flex.gap-2.mb-4")?.classList.add("hidden")
  }

  openPaywall() {
    window.dispatchEvent(new CustomEvent("show-paywall", { detail: { feature: "trends" } }))
  }

  async renderTrendsChart(trends) {
    if (!this.hasTrendsChartTarget) return

    // Ensure containers are visible
    const chartCard = this.trendsChartTarget.closest(".card") || this.trendsChartTarget.parentElement
    if (chartCard) chartCard.classList.remove("hidden")
    if (this.hasTrendsSummaryTarget) this.trendsSummaryTarget.classList.remove("hidden")
    if (this.hasTrendsPremiumGateTarget) this.trendsPremiumGateTarget.classList.add("hidden")
    // Show months switcher
    this.trendsViewTarget.querySelector(".flex.gap-2.mb-4")?.classList.remove("hidden")

    const Chart = this.getChart()
    const ctx = this.trendsChartTarget.getContext("2d")
    if (this.trendsChartInstance) this.trendsChartInstance.destroy()

    const locale = window.APP_LOCALE === "ru" ? "ru-RU" : "en-US"
    const labels = trends.map(t => {
      const [year, month] = t.month.split("-")
      const date = new Date(year, month - 1)
      return date.toLocaleDateString(locale, { month: "short" })
    })

    this.trendsChartInstance = new Chart(ctx, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: t("dashboard.expenses"),
            data: trends.map(t => t.expenses),
            backgroundColor: "rgba(239, 68, 68, 0.8)",
            borderRadius: { topLeft: 6, topRight: 6 },
            borderSkipped: "bottom",
            barPercentage: 0.7,
            categoryPercentage: 0.7
          },
          {
            label: t("dashboard.income"),
            data: trends.map(t => t.income),
            backgroundColor: "rgba(16, 185, 129, 0.8)",
            borderRadius: { topLeft: 6, topRight: 6 },
            borderSkipped: "bottom",
            barPercentage: 0.7,
            categoryPercentage: 0.7
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 600, easing: "easeOutCubic" },
        interaction: { mode: "index", intersect: false },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: this.hintColor, font: { size: 11, weight: "500" } },
            border: { display: false }
          },
          y: {
            grid: { color: "rgba(148, 163, 184, 0.08)", drawBorder: false },
            ticks: {
              color: this.hintColor,
              font: { size: 10 },
              maxTicksLimit: 5,
              callback: (v) => this.compactNumber(v)
            },
            border: { display: false },
            beginAtZero: true
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "rgba(0,0,0,0.85)",
            cornerRadius: 12,
            padding: 14,
            titleFont: { size: 13, weight: "600" },
            bodyFont: { size: 12 },
            bodySpacing: 6,
            callbacks: {
              label: (ctx) => {
                const label = ctx.datasetIndex === 0 ? `${t("dashboard.expenses")}` : `${t("dashboard.income")}`
                return `${label}: ${formatMoney(ctx.parsed.y, this.trendsCurrency)}`
              }
            }
          }
        }
      }
    })
  }

  renderTrendsSummary(trends) {
    if (!this.hasTrendsSummaryTarget) return

    const count = trends.length || 1
    const totalExp = trends.reduce((s, t) => s + t.expenses, 0)
    const totalInc = trends.reduce((s, t) => s + t.income, 0)
    const avgExp = totalExp / count
    const avgInc = totalInc / count

    this.trendsSummaryTarget.innerHTML = `
      <div class="card text-center" style="padding: 14px 12px;">
        <div class="text-xs font-medium mb-1" style="color: ${this.hintColor};">${t("stats.avg_expenses")}</div>
        <div class="text-lg font-bold" style="color: ${this.expenseColor}; letter-spacing: -0.3px;">
          ${formatMoney(avgExp, this.trendsCurrency)}
        </div>
      </div>
      <div class="card text-center" style="padding: 14px 12px;">
        <div class="text-xs font-medium mb-1" style="color: ${this.hintColor};">${t("stats.avg_income")}</div>
        <div class="text-lg font-bold" style="color: ${this.incomeColor}; letter-spacing: -0.3px;">
          ${formatMoney(avgInc, this.trendsCurrency)}
        </div>
      </div>
    `
  }

  renderTrendsBreakdown(trends) {
    if (!this.hasTrendsBreakdownTarget) return

    // Show breakdown target
    this.trendsBreakdownTarget.classList.remove("hidden")

    const locale = window.APP_LOCALE === "ru" ? "ru-RU" : "en-US"

    const rows = trends.slice().reverse().map(t => {
      const [year, month] = t.month.split("-")
      const date = new Date(year, month - 1)
      const monthName = date.toLocaleDateString(locale, { month: "long", year: "numeric" })
      const balance = t.income - t.expenses
      const balanceColor = balance >= 0 ? this.incomeColor : this.expenseColor
      const balanceSign = balance >= 0 ? "+" : ""

      return `
        <div class="flex items-center justify-between py-3 border-b" style="border-color: color-mix(in srgb, ${this.hintColor} 8%, transparent);">
          <div>
            <div class="text-sm font-medium" style="text-transform: capitalize;">${monthName}</div>
            <div class="flex gap-3 mt-1">
              <span class="text-xs" style="color: ${this.expenseColor};">-${formatMoney(t.expenses, this.trendsCurrency)}</span>
              <span class="text-xs" style="color: ${this.incomeColor};">+${formatMoney(t.income, this.trendsCurrency)}</span>
            </div>
          </div>
          <div class="text-sm font-semibold" style="color: ${balanceColor};">
            ${balanceSign}${formatMoney(balance, this.trendsCurrency)}
          </div>
        </div>`
    }).join("")

    this.trendsBreakdownTarget.innerHTML = rows
      ? `<div class="section-header">${t("stats.monthly_breakdown")}</div><div class="card">${rows}</div>`
      : ""
  }

  compactNumber(value) {
    if (value >= 1000000) return (value / 1000000).toFixed(1) + "M"
    if (value >= 1000) return (value / 1000).toFixed(0) + "K"
    return value.toString()
  }
}
